import { Injectable } from '@angular/core';
import { AccessoriesCartItem, CartDto, CartItem, ProductItemType, ShippingCartItem, SoilAnalysisCartItem } from '@as/api';
import { AuthStorageService } from '@farm-portal/core/auth/services/auth-storage.service';
import { buildShippingCartItem, createEmptyCart } from '@farm-portal/modules/shop-offers/common/utils/cart-items.mapper';
import { BehaviorSubject } from 'rxjs';
import { CART_STORAGE_KEY, CHECKOUT_IDEMPOTENCY_KEY, SHIPPING_PRODUCT_CODE, SOIL_BAGS_PRODUCT_CODE } from './models/cart.consts';
import { CheckoutIdempotencyState } from './models/idempotency-key.model';

type FingerprintItem = SoilFingerprintItem | AccessoriesFingerprintItem | ShippingFingerprintItem;

interface SoilFingerprintItem {
  kind: 'soil';
  productCode: string;
  provider: string;
  quantity: number;
  includeBags: boolean;
  summarizedAreaInHa: number;
  grounds: { id: string; areaInHa: number }[];
}

interface AccessoriesFingerprintItem {
  kind: 'accessories';
  productCode: string;
  quantity: number;
  parentCartItemId: string | null;
}

interface ShippingFingerprintItem {
  kind: 'shipping';
  productCode: string;
}

@Injectable({ providedIn: 'root' })
export class CartService {
  public currentCart$ = new BehaviorSubject<CartDto>(this.getCartSync());

  constructor(private readonly storage: AuthStorageService) {}

  public getCartSync(): CartDto {
    const raw = this.storage.getItemSync<CartDto>(CART_STORAGE_KEY) ?? createEmptyCart();
    const reconciled = this.reconcile(raw);

    const rawFp = this.buildCartFingerprint(raw);
    const reconciledFp = this.buildCartFingerprint(reconciled);
    if (rawFp !== reconciledFp) {
      this.storage.setItemSync(CART_STORAGE_KEY, reconciled);
    }
    return reconciled;
  }

  public setCartSync(cart: CartDto): void {
    const reconciled = this.reconcile({
      ...cart,
      updatedAt: new Date().toISOString()
    });
    this.storage.setItemSync(CART_STORAGE_KEY, reconciled);
    this.currentCart$.next(reconciled);
  }

  public addItemSync(item: CartItem): CartDto {
    const cart = this.getCartSync();
    const next: CartDto = { ...cart, updatedAt: new Date().toISOString(), items: [...cart.items, item] };
    const reconciled = this.reconcile(next);
    this.storage.setItemSync(CART_STORAGE_KEY, reconciled);
    this.currentCart$.next(reconciled);
    return reconciled;
  }

  public updateItemSync(itemId: string, updater: (current: CartItem) => CartItem): CartDto {
    const cart = this.getCartSync();
    const idx = cart.items.findIndex(x => x.id === itemId);
    if (idx === -1) return cart;

    const updatedItem = updater(cart.items[idx]);
    const next: CartDto = {
      ...cart,
      updatedAt: new Date().toISOString(),
      items: cart.items.map(x => (x.id === itemId ? updatedItem : x))
    };

    const reconciled = this.reconcile(next);
    this.storage.setItemSync(CART_STORAGE_KEY, reconciled);
    this.currentCart$.next(reconciled);
    return reconciled;
  }

  public removeItemSync(itemId: string): CartDto {
    const cart = this.getCartSync();
    const next: CartDto = {
      ...cart,
      updatedAt: new Date().toISOString(),
      items: cart.items.filter(x => x.id !== itemId)
    };
    const reconciled = this.reconcile(next);
    this.storage.setItemSync(CART_STORAGE_KEY, reconciled);
    this.currentCart$.next(reconciled);
    return reconciled;
  }

  public clearSync(): void {
    const cart = createEmptyCart();
    this.storage.setItemSync(CART_STORAGE_KEY, cart);
    this.currentCart$.next(cart);
  }

  public getOrCreateCheckoutIdempotencyKey(cart: CartDto): string {
    const existing = this.storage.getItemSync<CheckoutIdempotencyState>(CHECKOUT_IDEMPOTENCY_KEY);
    const ttlMs = 30 * 60 * 1000;
    const now = Date.now();
    const fingerprint = this.buildCartFingerprint(this.reconcile(cart));

    if (existing?.key && existing?.createdAt && existing?.cartFingerprint) {
      const createdAt = Date.parse(existing.createdAt);
      const isFresh = !Number.isNaN(createdAt) && now - createdAt <= ttlMs;
      if (isFresh && existing.cartFingerprint === fingerprint) {
        return existing.key;
      }
    }

    const next: CheckoutIdempotencyState = {
      key: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      cartFingerprint: fingerprint
    };
    this.storage.setItemSync(CHECKOUT_IDEMPOTENCY_KEY, next);
    return next.key;
  }

  public clearCheckoutIdempotencyKey(): void {
    this.storage.clearItem(CHECKOUT_IDEMPOTENCY_KEY);
  }

  private reconcile(cart: CartDto): CartDto {
    const items = [...(cart.items ?? [])];
    const soilItems = items.filter(this.isSoil);
    const accessoriesItems = items.filter(this.isAccessories);
    const shippingItems = items.filter(this.isShipping);
    const soilById = new Map<string, SoilAnalysisCartItem>(soilItems.map(s => [s.id, s]));

    const validAccessories = accessoriesItems.filter(a => {
      const parentId = a.details.parentCartItemId;
      if (!parentId) return false;
      const parent = soilById.get(parentId);
      if (!parent) return false;
      if (this.isBagsAccessory(a)) return parent.details.includeBags === true;
      return true;
    });

    const syncedAccessories = validAccessories
      .map(a => {
        const parent = soilById.get(a.details.parentCartItemId);
        const parentQty = parent?.details.bundle.quantity ?? 0;
        if (parentQty <= 0) return null;
        if (a.details.quantity === parentQty) return a;
        return {
          ...a,
          details: {
            ...a.details,
            quantity: parentQty
          }
        } as AccessoriesCartItem;
      })
      .filter((x): x is AccessoriesCartItem => x !== null);

    const hasAccessories = syncedAccessories.length > 0;
    let nextShipping: ShippingCartItem[] = [];
    if (hasAccessories) {
      const existing = shippingItems[0];
      nextShipping = [existing ?? buildShippingCartItem({ productCode: SHIPPING_PRODUCT_CODE })];
    }

    const base = items.filter(i => i.type !== ProductItemType.Accessories && i.type !== ProductItemType.Shipping);
    return {
      ...cart,
      updatedAt: new Date().toISOString(),
      items: [...base, ...syncedAccessories, ...nextShipping]
    };
  }

  private isSoil(item: CartItem): item is SoilAnalysisCartItem {
    return item.type === ProductItemType.SoilAnalysis;
  }

  private isAccessories(item: CartItem): item is AccessoriesCartItem {
    return item.type === ProductItemType.Accessories;
  }

  private isBagsAccessory(item: AccessoriesCartItem): boolean {
    return this.normalizeCode(item.details.productCode) === SOIL_BAGS_PRODUCT_CODE;
  }

  private isShipping(item: CartItem): item is ShippingCartItem {
    return item.type === ProductItemType.Shipping;
  }

  private buildCartFingerprint(cart: CartDto): string {
    const normalizedItems = cart.items.map(item => this.normalizeCartItemForFingerprint(item)).sort(this.compareFingerprintItems);
    return JSON.stringify({ version: cart.version, items: normalizedItems });
  }

  private normalizeCartItemForFingerprint(item: CartItem): FingerprintItem {
    switch (item.type) {
      case ProductItemType.SoilAnalysis: {
        const grounds = [...(item.details.selectedGrounds ?? [])]
          .map(g => ({ id: g.id, areaInHa: g.areaInHa }))
          .sort((a, b) => a.id.localeCompare(b.id));
        return {
          kind: 'soil',
          productCode: item.details.bundle.productCode,
          provider: item.details.provider,
          quantity: item.details.bundle.quantity,
          includeBags: item.details.includeBags,
          summarizedAreaInHa: item.details.summarizedAreaInHa,
          grounds
        };
      }
      case ProductItemType.Accessories:
        return {
          kind: 'accessories',
          productCode: item.details.productCode,
          quantity: item.details.quantity,
          parentCartItemId: item.details.parentCartItemId ?? null
        };
      case ProductItemType.Shipping:
        return {
          kind: 'shipping',
          productCode: item.details.productCode
        };
      default:
        return this.assertNever(item);
    }
  }

  private assertNever(x: CartItem): never {
    throw new Error(`Unsupported cart item type: ${x.type}`);
  }

  private compareFingerprintItems(a: FingerprintItem, b: FingerprintItem): number {
    if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
    if (a.productCode !== b.productCode) return a.productCode.localeCompare(b.productCode);
    switch (a.kind) {
      case 'soil':
        if (a.provider !== (b as SoilFingerprintItem).provider) return a.provider.localeCompare((b as SoilFingerprintItem).provider);
        if (a.quantity !== (b as SoilFingerprintItem).quantity) return a.quantity - (b as SoilFingerprintItem).quantity;
        if (a.includeBags !== (b as SoilFingerprintItem).includeBags) return a.includeBags ? 1 : -1;
        return 0;
      case 'accessories':
        if (a.parentCartItemId !== (b as AccessoriesFingerprintItem).parentCartItemId) {
          return (a.parentCartItemId ?? '').localeCompare((b as AccessoriesFingerprintItem).parentCartItemId ?? '');
        }
        return a.quantity - (b as AccessoriesFingerprintItem).quantity;
      case 'shipping':
        return 0;
    }
  }

  private normalizeCode(v: string): string {
    return (v ?? '').trim().toLowerCase();
  }
}
